import type { ContentfulCloudinaryAssetField } from "@project/ui";
import {
    AspectRatio,
    Box,
    CldImage,
    CldVideoPlayerProvider,
    CldVideoTogglePlay,
    Flex,
    Heading,
    hasCloudinaryAsset,
    isCloudinaryVideo,
    Text,
    CldVideo,
    Link,
} from "@project/ui";
import { WrapperContainer } from "./wrapper-container";
import type { ModuleSplitLayoutFieldsFragment } from "@/components/contentful/module-split-layout/__generated/module-split-layout.contentful.generated";
import { LinkWrapper } from "@/components/link-wrapper";
import type { PageCategoryFieldsFragment } from "@/components/contentful/page-category/__generated/page-category.contentful.generated";
import { AnimatingWrapper } from "./animating-wrapper";
import { useAppStore } from "@/store/app-store";

type MediaSplitProps = ModuleSplitLayoutFieldsFragment | PageCategoryFieldsFragment;

type MediaSplitLayoutProps = MediaSplitProps & {
    inViewAutoplay?: boolean;
};

const MediaSplitLayout = (props: MediaSplitLayoutProps) => {
    const {
        state: { hasLiveTicker },
    } = useAppStore();

    const asset = (
        props.__typename === "ModuleSplitLayout" ? props.asset : props.heroAsset
    ) as ContentfulCloudinaryAssetField;

    const reverse = props.__typename === "ModuleSplitLayout" ? props.reverse : false;

    const titleAsH1 = props.__typename !== "ModuleSplitLayout";

    return (
        <CldVideoPlayerProvider autoplay muted loop>
            <Flex
                className="MediaSplitLayout"
                overflow="hidden"
                flexDirection={{ base: "column", md: reverse ? "row-reverse" : "row" }}
                h={{ base: "auto", md: hasLiveTicker ? "screenHeightWithLiveTicker" : "100svh" }}
                bgColor="allWhite"
                position="relative"
            >
                <Box w={{ base: "100vw", md: "50vw" }} h="full">
                    <WrapperContainer
                        isSplitLayout={true}
                        h="full"
                        as={Flex}
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                    >
                        <Flex align="baseline" mb={8} alignItems="center" flexDir="column" flex={1}>
                            {props.introductionCaption && (
                                <AnimatingWrapper>
                                    <Text
                                        fontWeight="600"
                                        size="caption"
                                        color="motorsportsRed"
                                        mb={4}
                                    >
                                        {props.introductionCaption}
                                    </Text>
                                </AnimatingWrapper>
                            )}
                            <AnimatingWrapper delay={0.5}>
                                <Heading
                                    fontWeight="400"
                                    color="porscheBlack"
                                    size="headingXXLarge"
                                    as={titleAsH1 ? "h1" : "h2"}
                                    sx={{
                                        textWrap: "balance",
                                    }}
                                >
                                    {props.title}
                                </Heading>
                            </AnimatingWrapper>
                            {props.subtitle && (
                                <AnimatingWrapper delay={0.8}>
                                    <Text color="grey300" size="small" mt={8}>
                                        {props.subtitle}
                                    </Text>
                                </AnimatingWrapper>
                            )}
                            {props.__typename === "ModuleSplitLayout" && props.link && (
                                <LinkWrapper
                                    mt={8}
                                    renderAs={Link}
                                    item={props.link}
                                    variant="secondary"
                                >
                                    {props.linkLabel ? props.linkLabel : null}
                                </LinkWrapper>
                            )}
                        </Flex>
                    </WrapperContainer>
                </Box>
                {hasCloudinaryAsset(asset) && (
                    <Box pos="relative" w={{ base: "100vw", md: "50vw" }} h="full">
                        <AspectRatio
                            ratio={"7:6"}
                            display={{ base: "block", md: "none" }}
                            pos="relative"
                        >
                            {isCloudinaryVideo(asset) ? (
                                <CldVideo
                                    cloudinaryAsset={asset}
                                    wrapperProps={{
                                        position: "absolute",
                                        borderRadius: "unset",
                                    }}
                                    loop
                                    autoPlay
                                    inViewAutoplay={props.inViewAutoplay}
                                />
                            ) : (
                                <CldImage
                                    cloudinaryAsset={asset}
                                    priority={true}
                                    fill
                                    wrapperProps={{
                                        position: "absolute",
                                        borderRadius: "unset",
                                    }}
                                />
                            )}
                        </AspectRatio>
                        <Box
                            display={{ base: "none", md: "block" }}
                            width={"50vw"}
                            h={hasLiveTicker ? "screenHeightWithLiveTicker" : "100svh"}
                            pos="relative"
                        >
                            {isCloudinaryVideo(asset) ? (
                                <CldVideo
                                    cloudinaryAsset={asset}
                                    wrapperProps={{
                                        position: "absolute",
                                        borderRadius: "unset",
                                    }}
                                    loop
                                    autoPlay
                                    inViewAutoplay={props.inViewAutoplay}
                                />
                            ) : (
                                <CldImage
                                    cloudinaryAsset={asset}
                                    priority={true}
                                    fill
                                    wrapperProps={{
                                        position: "absolute",
                                        borderRadius: "unset",
                                    }}
                                />
                            )}
                        </Box>
                        {hasCloudinaryAsset(asset) && isCloudinaryVideo(asset) && (
                            <AnimatingWrapper>
                                <CldVideoTogglePlay
                                    sx={{
                                        position: "absolute",
                                        right: ["var(--space-5)", "var(--space-10)"],
                                        bottom: ["var(--space-5)", "var(--space-10)"],
                                    }}
                                />
                            </AnimatingWrapper>
                        )}
                    </Box>
                )}
            </Flex>
        </CldVideoPlayerProvider>
    );
};

export { MediaSplitLayout };
